// Painted enemy frames: the AI-painted cutout parts (src/art/foes) drawn onto the existing
// pose skeletons from rig.ts, so every animation, attachment point and gameplay value stays
// exactly as before - only the art changes. Each limb image is fitted between its two joints
// (hip→knee, knee→ankle); heads / bodies are placed by their eye pivot so the runtime sensor
// flare (frameMeta.sensor) lands on the painted visor.

import { COLORS, css } from '../../config/palette';
import type { EnemyId } from '../../core/types';
import { darkened, dirOf, drawAt, drawBone, pivot, span, type FoePart, type FoeRig, type Pt } from '../../art/foes';
import { RIGS, bulwarkPose, hornetPose, ik2, lancerPose, setFrameMetaSource, trooperPose, type BipedPose, type EnemyAnim, type HornetPose, type LegPose } from './rig';

type G = CanvasRenderingContext2D;
type V = { x: number; y: number };

const TAU = Math.PI * 2;

function glow(g: G, x: number, y: number, r: number, color: number, a: number): void {
  if (a <= 0.01) return;
  const grad = g.createRadialGradient(x, y, 0, x, y, r * 3);
  grad.addColorStop(0, css(0xffffff, Math.min(1, a)));
  grad.addColorStop(0.25, css(color, Math.min(1, a * 0.85)));
  grad.addColorStop(1, css(color, 0));
  g.save();
  g.globalCompositeOperation = 'lighter';
  g.fillStyle = grad;
  g.beginPath();
  g.arc(x, y, r * 3, 0, TAU);
  g.fill();
  g.restore();
}

export interface PaintedEnemy {
  body: (g: G, gl: G, anim: EnemyAnim, i: number) => void;
  gun?: (g: G, gl: G) => void;
  shield?: (g: G, gl: G, mode: 'up' | 'bash' | 'down') => void;
  gib: (g: G, gl: G, k: number) => void;
}

/** Draw a part scaled to fit a size×size box centred on the origin (debris). */
function fitDraw(g: G, part: FoePart, size: number, rot = 0): void {
  const k = (size * 0.86) / Math.max(part.w, part.h);
  g.save();
  g.rotate(rot);
  g.scale(k, k);
  g.drawImage(part.img, -part.w / 2, -part.h / 2);
  g.restore();
}

// ───────────────────────────── Bipeds ─────────────────────────────
//
// Legs follow the pose skeleton exactly (each limb image fitted hip→knee→ankle). The upper body
// is a chain of painted pivots: torso (waist on the hip, leaning with the pose), head on the
// torso's neck, gun arm / shield arm on the torso's shoulder - so it keeps the painting's
// proportions. The type's attachment points (gun pivot, sensor, head top) are re-published
// from that chain (setFrameMetaSource) so the runtime aims, flares and labels line up.

interface BipedTune {
  torso: number;
  head: number;
  gun: number;
  pelvis: number;
  foot: number;
  /** Torso waist offset from the pose hip (rig px). */
  waistX: number;
  waistY: number;
}

const POSE = { trooper: trooperPose, lancer: lancerPose, bulwark: bulwarkPose } as const;

/** A part placement: pivot `a` of `part` at (x, y), rotated, scaled. */
class Xf {
  constructor(
    readonly part: FoePart,
    readonly a: string,
    readonly x: number,
    readonly y: number,
    readonly rot: number,
    readonly k: number,
  ) {}
  /** World position of a pivot (or any part pixel) of this part. */
  at(p: string | Pt): V {
    const [ax, ay] = pivot(this.part, this.a);
    const [px, py] = typeof p === 'string' ? pivot(this.part, p) : p;
    const dx = (px - ax) * this.k;
    const dy = (py - ay) * this.k;
    const c = Math.cos(this.rot);
    const s = Math.sin(this.rot);
    return { x: this.x + dx * c - dy * s, y: this.y + dx * s + dy * c };
  }
  draw(g: G, img?: CanvasImageSource): void {
    drawAt(g, this.part, this.a, this.x, this.y, this.rot, this.k, img);
  }
}

function biped(id: 'trooper' | 'lancer' | 'bulwark', rig: FoeRig): PaintedEnemy {
  const P = rig.parts;
  const t = rig.tune;
  const T: BipedTune = {
    torso: t.torso ?? 1,
    head: t.head ?? 1,
    gun: t.gun ?? 1,
    pelvis: t.pelvis ?? 1,
    foot: t.foot ?? 1,
    waistX: t.waistX ?? 0,
    waistY: t.waistY ?? -2,
  };
  const pose = POSE[id];
  // Body scale: the thigh image fitted to the idle thigh bone.
  const p0 = pose('idle', 0);
  const k = Math.hypot(p0.legF.knee.x - p0.legF.hip.x, p0.legF.knee.y - p0.legF.hip.y) / Math.max(1, span(P.thigh, 'hip', 'knee'));
  const lean0 = Math.atan2(p0.head.y - p0.hip.y, p0.head.x - p0.hip.x);

  /** Arm scale (bulwark: its arms are as massive as its torso). */
  const armK = k * T.torso * (t.arm ?? 1);
  /** Piston-arm scale (set from the body below, once the chain exists). */
  let bashK = armK;
  // Bulwark front arm: the upper arm hangs from under the shoulder armour, the forearm reaches
  // forward to the shield grip (the shield is attached there - see the frame meta below).
  const upperLen = P.upperarm ? span(P.upperarm, 'shoulder', 'elbow') * armK : 0;
  const foreLen = P.forearm ? span(P.forearm, 'elbow', 'grip') * armK : 0;

  /** The upper-body chain of one pose. */
  const chain = (B: BipedPose) => {
    const lean = Math.atan2(B.head.y - B.hip.y, B.head.x - B.hip.x) - lean0;
    const torso = new Xf(P.torso, 'waist', B.hip.x + T.waistX, B.hip.y + T.waistY, lean, k * T.torso);
    // A separate head rides on the torso's neck; otherwise the torso includes the head.
    let head: Xf | null = null;
    if (P.head) {
      const neck = torso.at('neck');
      head = new Xf(P.head, 'neck', neck.x, neck.y, lean * 0.5 + B.headTilt, k * T.head);
    }
    const eyeOf = head ?? torso;
    const shoulder = torso.at('shoulder');
    let elbow = shoulder;
    let grip = B.grip;
    if (id === 'bulwark') {
      // Far-side shield arm: the fist holds the shield's back edge at chest height, close in
      // front of the body; the elbow is solved by 2-bone IK (bending down / back).
      const thrust = B.grip.x - (B.hip.x + 34 + B.lean);
      grip = { x: shoulder.x + upperLen * (t.gripX ?? 0.95) + thrust, y: shoulder.y + upperLen * (t.gripY ?? 0.45) };
      elbow = ik2(shoulder, grip, upperLen, foreLen, 1).mid;
      grip = ik2(shoulder, grip, upperLen, foreLen, 1).end;
    }
    return { lean, torso, head, eyeOf, shoulder, elbow, grip };
  };

  const gunK = k * T.gun;
  const gunRig = RIGS[id].gun;
  if (gunRig) {
    // The painted barrel is horizontal in its image: the muzzle sits where the painting puts it.
    const [sx, sy] = pivot(P.gun, 'shoulder');
    const [mx, my] = pivot(P.gun, 'muzzle');
    gunRig.muzzleX = (mx - sx) * gunK;
    gunRig.muzzleY = (my - sy) * gunK;
  }

  setFrameMetaSource(id, (anim, i) => {
    const B = pose(anim, i);
    const c = chain(B);
    const eye = c.eyeOf.at('eye');
    const [ex] = pivot(c.eyeOf.part, 'eye');
    const top = c.eyeOf.at([ex, 0]).y;
    return { pivot: id === 'bulwark' ? c.grip : c.shoulder, sensor: eye, top };
  });

  /** Limb bulk: >1 draws the leg images larger than their bone fit (they overdraw past the joints). */
  const limb = t.limb ?? 1;
  const leg = (g: G, L: LegPose, far: boolean): void => {
    drawBone(g, P.thigh, 'hip', 'knee', L.hip, L.knee, far ? darkened(P.thigh) : undefined, limb);
    drawBone(g, P.shin, 'knee', 'ankle', L.knee, L.ankle, far ? darkened(P.shin) : undefined, limb);
    drawAt(g, P.foot, 'ankle', L.ankle.x, L.ankle.y, L.foot, k * T.foot, far ? darkened(P.foot) : undefined);
  };

  const body = (g: G, gl: G, anim: EnemyAnim, i: number): void => {
    const B = pose(anim, i);
    const c = chain(B);
    leg(g, B.legR, true);
    const pelvis = () => drawAt(g, P.pelvis, 'hip', B.hip.x, B.hip.y, 0, k * T.pelvis);
    // The hunched bulwark's torso overhangs its hips; the others wear the pelvis over the waist.
    if (id === 'bulwark') pelvis();
    if (id === 'bulwark') {
      // Far-side shield arm: entirely behind the torso (and the shield sprite drawn over it),
      // shaded like the far leg - only the gripping fist shows at the shield's edge.
      drawBone(g, P.upperarm, 'shoulder', 'elbow', c.shoulder, c.elbow, darkened(P.upperarm, 0.3));
      drawBone(g, P.forearm, 'elbow', 'grip', c.elbow, c.grip, darkened(P.forearm, 0.2));
    }
    c.torso.draw(g);
    if (id !== 'bulwark') pelvis();
    leg(g, B.legF, false);
    c.head?.draw(g);
    if (id === 'bulwark') {
      // Near-side piston arm, two bones: hangs down-back with a natural elbow bend, cocks back on
      // the wind-up and swings forward on the bash.
      const b = c.torso.at('bash');
      const sway = 0.05 * Math.sin(i * 1.6);
      const [up, lo] =
        anim === 'windup' ? [0.55 + i * 0.12, 0.35 + i * 0.1] : anim === 'attack' ? [-0.45, -0.95] : anim === 'stagger' ? [0.45, 0.1] : [0.5 + sway, -0.62 + sway];
      const upDir = Math.PI / 2 + up + c.lean * 0.6;
      const loDir = Math.PI / 2 + lo + c.lean * 0.6;
      drawAt(g, P.bashupper, 'shoulder', b.x, b.y, upDir - dirOf(P.bashupper, 'shoulder', 'elbow'), bashK);
      const ul = span(P.bashupper, 'shoulder', 'elbow') * bashK;
      const e = { x: b.x + Math.cos(upDir) * ul, y: b.y + Math.sin(upDir) * ul };
      drawAt(g, P.bashfore, 'elbow', e.x, e.y, loDir - dirOf(P.bashfore, 'elbow', 'fist'), bashK);
      const v = c.torso.at('vent');
      glow(gl, v.x, v.y, 3, COLORS.threatHot, 0.3 * B.extra);
    }
    if (id === 'lancer' && B.extra > 0.05) {
      // Calf jump-jets.
      for (const L of [B.legR, B.legF]) {
        const jx = L.knee.x + (L.ankle.x - L.knee.x) * 0.45 - 3.5;
        const jy = L.knee.y + (L.ankle.y - L.knee.y) * 0.45 + 5;
        flame(gl, jx, jy, 12 * B.extra, B.extra);
      }
    }
    const eye = c.eyeOf.at('eye');
    glow(gl, eye.x, eye.y, id === 'bulwark' ? 1.5 : 1.3, COLORS.threat, 0.5 * B.sensor);
  };

  const out: PaintedEnemy = {
    body,
    gib: (g, _gl, n) => {
      const size = Math.round(Math.max(RIGS[id].cellW, RIGS[id].cellH) * 0.45);
      if (n === 0) fitDraw(g, P.head ?? P.forearm, size * 0.8);
      else if (n === 1) fitDraw(g, P.bashfore ?? P.torso, size, 0.3);
      else fitDraw(g, P.shin, size, 0.5);
    },
  };

  if (gunRig) {
    out.gun = (g, gl) => {
      drawAt(g, P.gun, 'shoulder', 0, 0, 0, gunK);
      glow(gl, gunRig.muzzleX - 0.5, gunRig.muzzleY, 1.1, COLORS.threat, 0.45);
    };
  }

  const sh = RIGS[id].shield;
  // Idle grip and head top (the shield is sized from them).
  const idle = chain(pose('idle', 0));
  // Piston arm scale: hanging from its shoulder, the fist reaches about knee height (concept).
  if (P.bashupper && P.bashfore) {
    const ip = pose('idle', 0);
    const shoulderY = idle.torso.at('bash').y;
    const kneeY = (ip.legF.knee.y + ip.legR.knee.y) / 2;
    const reach = (kneeY - shoulderY) * (t.bashReach ?? 1) / 0.93;
    bashK = reach / (span(P.bashupper, 'shoulder', 'elbow') + span(P.bashfore, 'elbow', 'fist'));
  }
  const idleGrip = idle.grip;
  const slabTop = idle.eyeOf.at([pivot(idle.eyeOf.part, 'eye')[0], 0]).y - (rig.tune.shieldOver ?? 6);
  if (sh) {
    out.shield = (g, gl, mode) => {
      const fwd = mode === 'bash' ? 3 : 0;
      // Slab centred just past the grip: the fist holds it from behind.
      const cx = sh.planeX0 - 2 + fwd;
      // A solid riot slab standing from the ground to above the head (relative to the grip,
      // which sits at the fist): sized from the idle pose so it matches the painted body.
      const top = { x: cx, y: slabTop - idleGrip.y };
      const bot = { x: cx, y: -1 - idleGrip.y };
      const sk = (bot.y - top.y) / (sh.planeY1 - sh.planeY0);
      drawBone(g, P.shield, 'top', 'bottom', top, bot);
      if (mode !== 'bash') return;
      // Bash: the slab's red emitter strip flares (it sits ~85% across the painted slab).
      const kk = (bot.y - top.y) / Math.max(1, span(P.shield, 'top', 'bottom'));
      const [tx, ty] = pivot(P.shield, 'top');
      const sx = cx + (P.shield.w * 0.85 - tx) * kk;
      const y0 = top.y + (P.shield.h * 0.33 - ty) * kk;
      const y1 = top.y + (P.shield.h * 0.8 - ty) * kk;
      gl.save();
      gl.globalCompositeOperation = 'lighter';
      gl.strokeStyle = css(COLORS.threat, 0.7);
      gl.lineWidth = 3;
      gl.shadowColor = css(COLORS.threat);
      gl.shadowBlur = 8;
      gl.beginPath();
      gl.moveTo(sx, y0);
      gl.lineTo(sx, y1);
      gl.stroke();
      gl.restore();
    };
  }
  return out;
}


function flame(g: G, x: number, y: number, len: number, a: number): void {
  g.save();
  g.globalCompositeOperation = 'lighter';
  const grad = g.createLinearGradient(x, y, x, y + len);
  grad.addColorStop(0, css(0xffe0b0, 0.95 * a));
  grad.addColorStop(0.35, css(COLORS.threatHot, 0.8 * a));
  grad.addColorStop(1, css(COLORS.threatHot, 0));
  g.fillStyle = grad;
  g.beginPath();
  g.moveTo(x - 2.4, y);
  g.lineTo(x + 2.4, y);
  g.lineTo(x, y + len);
  g.closePath();
  g.fill();
  g.restore();
}

// ───────────────────────────── Hornet ─────────────────────────────

function hornet(rig: FoeRig): PaintedEnemy {
  const P = rig.parts;
  const t = rig.tune;
  // Body scale from its overall length (pose units: tail of thorax -12.5 … eye 22.5).
  const k = (t.bodyLen ?? 34) / Math.max(1, P.body.w);
  const eye: V = { x: t.eyeX ?? 18.5, y: t.eyeY ?? 0 };
  const at = (name: string): V => {
    const [ex, ey] = pivot(P.body, 'eye');
    const [px, py] = pivot(P.body, name);
    return { x: eye.x + (px - ex) * k, y: eye.y + (py - ey) * k };
  };
  const wingLen = (front: boolean) => (front ? t.wingF ?? 26 : t.wingR ?? 23);

  const wing = (g: G, ang: number, front: boolean, blur: number): void => {
    const root = at('wing');
    const kw = wingLen(front) / Math.max(1, span(P.wing, 'root', 'tip'));
    const rot = ang - dirOf(P.wing, 'root', 'tip');
    const img = front ? P.wing.img : darkened(P.wing, 0.25);
    const base = front ? 0.82 : 0.6;
    if (blur > 0.05) {
      g.save();
      g.globalAlpha = base * 0.35;
      drawAt(g, P.wing, 'root', root.x, root.y + (front ? 0 : 1), rot - 0.26 * blur, kw, img);
      drawAt(g, P.wing, 'root', root.x, root.y + (front ? 0 : 1), rot + 0.26 * blur, kw, img);
      g.restore();
    }
    g.save();
    g.globalAlpha = base;
    drawAt(g, P.wing, 'root', root.x, root.y + (front ? 0 : 1), rot, kw, img);
    g.restore();
  };

  const draw = (g: G, gl: G, H: HornetPose): void => {
    for (const c of [g, gl]) {
      c.save();
      c.translate(0, H.bob);
      c.rotate(H.tilt);
    }
    wing(g, H.wingR, false, H.blur);

    // Abdomen: two segments chained from the thorax joint, curling under during the wind-up.
    const a1 = 2.72 - H.curl * 0.62;
    const a2 = 2.86 - H.curl * 1.45;
    const j0 = at('abd');
    const ka = k * (t.abd ?? 0.75);
    const l1 = span(P.abd, 'front', 'back') * ka;
    const j1 = { x: j0.x + Math.cos(a1) * l1, y: j0.y + Math.sin(a1) * l1 };
    const lt = span(P.tail, 'front', 'tip') * ka;
    drawAt(g, P.tail, 'front', j1.x, j1.y, a2 - dirOf(P.tail, 'front', 'tip'), ka);
    drawAt(g, P.abd, 'front', j0.x, j0.y, a1 - dirOf(P.abd, 'front', 'back'), ka);
    const tip = { x: j1.x + Math.cos(a2) * lt, y: j1.y + Math.sin(a2) * lt };
    glow(gl, tip.x, tip.y, 1.2, COLORS.threat, 0.5 * H.sensor);

    const lm = at('legs');
    drawAt(g, P.legs, 'mount', lm.x, lm.y, H.tilt * -0.3, k);

    // Forward drill spike slides out of the head (the dive leads with it).
    const sm = at('spike');
    const sl = 7 + H.sting * 11;
    const len = span(P.spike, 'mount', 'tip') * k;
    const d = Math.max(0, sl - len);
    drawAt(g, P.spike, 'mount', sm.x + Math.cos(H.stingAng) * d, sm.y + Math.sin(H.stingAng) * d, H.stingAng - dirOf(P.spike, 'mount', 'tip'), k);
    if (H.sting > 0.5) glow(gl, sm.x + Math.cos(H.stingAng) * Math.max(sl, len), sm.y + Math.sin(H.stingAng) * Math.max(sl, len), 1.3, COLORS.threatHot, (H.sting - 0.5) * 1.6);

    drawAt(g, P.body, 'eye', eye.x, eye.y, 0, k);
    glow(gl, eye.x, eye.y, 2.2, COLORS.threat, 0.6 * H.sensor);

    wing(g, H.wingF, true, H.blur);
    for (const c of [g, gl]) c.restore();
  };

  return {
    body: (g, gl, anim, i) => draw(g, gl, hornetPose(anim, i)),
    gib: (g, _gl, n) => {
      const size = Math.round(Math.max(RIGS.hornet.cellW, RIGS.hornet.cellH) * 0.45);
      if (n === 0) fitDraw(g, P.wing, size);
      else if (n === 1) fitDraw(g, P.body, size * 0.8);
      else fitDraw(g, P.tail, size, 0.4);
    },
  };
}

// ───────────────────────────── Setup-pose rigs ─────────────────────────────
//
// The professional "slice the illustration" rig: pieces are cut straight out of the concept art
// (tools/cut-poly.mjs) and placed back exactly where they were, so the rest pose IS the concept.
// Animation is small rotations about real joints from that pose: legs by 2-bone IK toward the
// pose skeleton's foot targets (feet plant and step), the torso leans from the waist, the free
// arm is a 2-bone chain. Pose deltas come from the procedural pose (rig.ts) relative to its idle,
// so timing and gameplay stay identical.
//
// Optional joints: <side>Sole (foot bottom - the leg's rest target is moved so both soles stand on
// the ground line), <side>Jet (calf jet nozzle, flames on the leap), gunPivot + barrel + muzzle (a
// "gun" piece - weapon with the arms holding it - becomes the runtime gun sprite, aimed about
// gunPivot), elbow (free 2-bone arm), grip (shield / gun attachment). Tune: crouch lowers the hips
// a little in every pose so near-straight painted legs keep enough reach to step.

type Pt2 = readonly [number, number];
interface SetupJson {
  mode: 'setup';
  /** Draw order, back to front (the shield is its own sprite). */
  order: string[];
  pieces: Record<string, { origin: Pt2 }>;
  joints: Record<string, Pt2>;
}

/** 2D affine (a c e / b d f). */
type M = [number, number, number, number, number, number];
const mul = (m: M, n: M): M => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5],
];
const tr = (x: number, y: number): M => [1, 0, 0, 1, x, y];
const rot = (a: number): M => [Math.cos(a), Math.sin(a), -Math.sin(a), Math.cos(a), 0, 0];
const apply = (m: M, p: V): V => ({ x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] });
/** Rotate by `a` about bind point `pivot`, landing it on `at`. */
const about = (pivot: V, at: V, a: number, k = 1): M =>
  mul(tr(at.x, at.y), mul(rot(a), mul([k, 0, 0, k, 0, 0], tr(-pivot.x, -pivot.y))));

function setupRig(id: EnemyId, rig: FoeRig): PaintedEnemy {
  const J = rig.json as unknown as SetupJson;
  const P = rig.parts;
  const t = rig.tune;
  const ground = t.ground ?? 1365;
  const rootX = t.rootX ?? 450;
  const top = t.top ?? 215;
  // Concept pixels → rig units (feet on y = 0, figure `height` tall).
  const s = (t.height ?? 66) / (ground - top);
  const base: M = [s, 0, 0, s, -rootX * s, -ground * s];
  const j = (n: string): V => apply(base, { x: J.joints[n][0], y: J.joints[n][1] });
  const pose = id === 'bulwark' ? bulwarkPose : id === 'lancer' ? lancerPose : trooperPose;
  const idle = pose('idle', 0);

  /** Draw a piece at its setup position, through `m` (rig units). */
  const piece = (g: G, name: string, m: M): void => {
    const p = P[name];
    const o = J.pieces[name]?.origin;
    if (!p || !o) return;
    const w = mul(m, base);
    g.save();
    g.transform(w[0], w[1], w[2], w[3], w[4], w[5]);
    g.drawImage(p.img, o[0], o[1]);
    g.restore();
  };

  // Side-view legs (tune.sideLegs): instead of the painting's 3/4 straddle (near leg out to one side,
  // far leg to the other, far knee bent backwards), each foot rests under its own hip, both knees
  // bend forward and the legs can be scaled (tune.legScale); the hips drop to keep a slight bend.
  const sideLegs = !!t.sideLegs;
  const LK = t.legScale ?? 1;

  // Leg IK: bind lengths + the bend side the painting has (forward in side-view mode).
  const leg = (side: 'front' | 'back') => {
    const hip = j(`${side}Hip`);
    const knee = j(`${side}Knee`);
    const ankle = j(`${side}Ankle`);
    const l1 = Math.hypot(knee.x - hip.x, knee.y - hip.y) * LK;
    const l2 = Math.hypot(ankle.x - knee.x, ankle.y - knee.y) * LK;
    if (sideLegs) {
      const fwd = ik2(hip, { x: hip.x + 0.1, y: hip.y + (l1 + l2) * 0.9 }, l1, l2, 1).mid;
      const bend: 1 | -1 = fwd.x > hip.x ? 1 : -1;
      const sole = J.joints[`${side}Sole`] ? j(`${side}Sole`) : ankle;
      const rest = { x: hip.x + (t.footFwd ?? 0.4), y: -(sole.y - ankle.y) * LK };
      return { hip, knee, ankle, rest, l1, l2, bend };
    }
    // Bend side: which side of the hip→ankle line the painted knee sits on.
    const cross = (ankle.x - hip.x) * (knee.y - hip.y) - (ankle.y - hip.y) * (knee.x - hip.x);
    const a = ik2(hip, ankle, l1, l2, 1).mid;
    const a2 = ik2(hip, { x: ankle.x, y: ankle.y - 0.5 }, l1, l2, 1).mid;
    const probe = (a.x - hip.x) * (ankle.y - hip.y) - (a.y - hip.y) * (ankle.x - hip.x);
    const probe2 = (a2.x - hip.x) * (ankle.y - hip.y) - (a2.y - hip.y) * (ankle.x - hip.x);
    const sideOf1 = Math.abs(probe) > 1e-6 ? -Math.sign(probe) : -Math.sign(probe2);
    const bend: 1 | -1 = Math.sign(cross) === sideOf1 || cross === 0 ? 1 : -1;
    // Rest target: the painted ankle, moved so the sole stands on the ground line (y = 0).
    const sole = J.joints[`${side}Sole`] ? j(`${side}Sole`) : null;
    const rest = { x: ankle.x, y: ankle.y - (sole ? sole.y : 0) };
    return { hip, knee, ankle, rest, l1, l2, bend };
  };
  const LEGS = { front: leg('front'), back: leg('back') };
  // Side-view legs: lower the hips until the shorter legs reach their feet with a slight bend.
  const autoCrouch = sideLegs
    ? (['front', 'back'] as const).reduce((sum, side) => {
        const L = LEGS[side];
        const reach = (L.l1 + L.l2) * 0.95;
        const need = L.rest.y - Math.sqrt(Math.max(0, reach * reach - (L.rest.x - L.hip.x) ** 2));
        return sum + (need - L.hip.y) / 2;
      }, 0)
    : 0;

  const frame = (anim: EnemyAnim, i: number) => {
    const B = pose(anim, i);
    const crouch = (t.crouch ?? 0) + autoCrouch;
    const dHip =
      anim === 'move'
        ? { x: 0, y: crouch + (t.bob ?? 1.6) * Math.abs(Math.cos((i / 8) * Math.PI * 2)) - (t.bob ?? 1.6) * 0.5 }
        : { x: B.hip.x - idle.hip.x, y: crouch + B.hip.y - idle.hip.y };
    const lean = (B.lean - idle.lean) * (t.leanK ?? 0.03) + B.headTilt * 0.6;
    const waist = j('waist');
    const torso = about(waist, { x: waist.x + dHip.x, y: waist.y + dHip.y }, lean);
    const legs = (['front', 'back'] as const).map((side) => {
      const L = LEGS[side];
      const P0 = side === 'front' ? idle.legF : idle.legR;
      const P1 = side === 'front' ? B.legF : B.legR;
      const hip = { x: L.hip.x + dHip.x, y: L.hip.y + dHip.y };
      // Walk: each foot steps around its painted position (opposite phases), lifting only while it
      // swings forward - the stance stays as wide as in the painting. Other anims use the pose's
      // foot offsets (wider brace on the wind-up / bash).
      let target: V;
      let footRot = P1.foot - P0.foot;
      if (anim === 'move') {
        const ph = (i / 8) * Math.PI * 2 + (side === 'front' ? 0 : Math.PI);
        const lift = Math.max(0, -Math.sin(ph));
        target = { x: L.rest.x + (t.stride ?? 7) * Math.cos(ph), y: L.rest.y - (t.lift ?? 5) * lift };
        footRot = -0.18 * lift * Math.sign(Math.cos(ph) || 1);
      } else if (anim === 'leap' && t.tuck) {
        // Airborne: both feet draw up under the hips (knees forward), jets pointing down.
        const k = t.tuck * (0.85 + 0.15 * Math.sin(i * 2));
        target = { x: L.rest.x + (hip.x - L.rest.x) * k * 0.15, y: L.rest.y + (hip.y - L.rest.y) * k };
        footRot = side === 'front' ? 0.35 : 0.2;
      } else {
        // Pose foot offsets, scaled by tune.footK (0 keeps a planted stance - wide painted stances
        // would cross their legs with a narrow skeleton's foot motion).
        const fk = t.footK ?? 1;
        target = { x: L.rest.x + (P1.ankle.x - P0.ankle.x) * fk, y: L.rest.y + (P1.ankle.y - P0.ankle.y) * fk };
        footRot *= fk;
      }
      const r = ik2(hip, target, L.l1, L.l2, L.bend);
      const ang = (a: V, b: V) => Math.atan2(b.y - a.y, b.x - a.x);
      return {
        side,
        thigh: about(L.hip, hip, ang(hip, r.mid) - ang(L.hip, L.knee), LK),
        shin: about(L.knee, r.mid, ang(r.mid, r.end) - ang(L.knee, L.ankle), LK),
        foot: about(L.ankle, r.end, footRot, LK),
      };
    });
    // Free arm: shoulder rides the torso; cocks back on the wind-up, swings on the bash.
    const sway = 0.04 * Math.sin(i * 1.6);
    const [up, lo] =
      anim === 'windup' ? [0.25 + i * 0.1, 0.15 + i * 0.08] : anim === 'attack' ? [-0.4, -0.55] : anim === 'stagger' ? [0.3, 0.2] : anim === 'move' ? [0.08 * Math.sin((i / 8) * Math.PI * 2), 0.05 * Math.sin((i / 8) * Math.PI * 2)] : [sway, sway * 0.5];
    if (!J.joints.elbow) return { B, torso, legs, upper: null, fore: null };
    const sh = apply(torso, j('shoulder'));
    const upper = about(j('shoulder'), sh, lean + up);
    const el = apply(upper, j('elbow'));
    const fore = about(j('elbow'), el, lean + up + lo);
    return { B, torso, legs, upper, fore };
  };

  const order = J.order;
  const body = (g: G, gl: G, anim: EnemyAnim, i: number): void => {
    const F = frame(anim, i);
    const L = Object.fromEntries(F.legs.map((l) => [l.side, l]));
    const xf: Record<string, M | null> = {
      backThigh: L.back.thigh,
      backShin: L.back.shin,
      backFoot: L.back.foot,
      frontThigh: L.front.thigh,
      frontShin: L.front.shin,
      frontFoot: L.front.foot,
      armUpper: F.upper,
      armFore: F.fore,
      torso: F.torso,
    };
    for (const n of order) {
      const m = xf[n];
      if (m) piece(g, n, m);
    }
    // Calf jump-jets (the leap).
    if (F.B.extra > 0.05) {
      for (const l of F.legs) {
        const jet = J.joints[`${l.side}Jet`];
        if (!jet) continue;
        const p = apply(l.shin, j(`${l.side}Jet`));
        flame(gl, p.x, p.y, 12 * F.B.extra, F.B.extra);
      }
    }
    const eye = apply(F.torso, j('eye'));
    glow(gl, eye.x, eye.y, 1.5, COLORS.threat, 0.5 * F.B.sensor);
    if (J.joints.vent) {
      const v = apply(F.torso, j('vent'));
      glow(gl, v.x, v.y, 3, COLORS.threatHot, 0.3 * F.B.extra);
    }
  };

  setFrameMetaSource(id, (anim, i) => {
    const F = frame(anim, i);
    const eye = apply(F.torso, j('eye'));
    const topY = apply(F.torso, apply(base, { x: J.joints.eye[0], y: top })).y;
    if (J.joints.gunPivot) return { pivot: apply(F.torso, j('gunPivot')), sensor: eye, top: topY };
    const grip = apply(F.torso, j('grip'));
    return { pivot: { x: grip.x + (F.B.grip.x - idle.grip.x) * 0.5, y: grip.y }, sensor: eye, top: topY };
  });

  const out: PaintedEnemy = {
    body,
    gib: (g, _gl, n) => {
      const size = Math.round(Math.max(RIGS[id].cellW, RIGS[id].cellH) * 0.45);
      fitDraw(g, P[n === 0 ? 'torso' : n === 1 ? 'armFore' : 'frontShin'] ?? P.torso, size, n * 0.3);
    },
  };

  const gunRig = RIGS[id].gun;
  if (gunRig && P.gun && J.joints.gunPivot && J.joints.muzzle) {
    // Weapon + the arms holding it, aimed about the shoulder: the painted barrel is levelled so
    // aim angle 0 shoots straight ahead, and the runtime muzzle is where the painting puts it.
    const gp = j('gunPivot');
    const mz = j('muzzle');
    const rear = J.joints.barrel ? j('barrel') : gp;
    const level: M = mul(rot(-Math.atan2(mz.y - rear.y, mz.x - rear.x)), tr(-gp.x, -gp.y));
    const m = apply(level, mz);
    gunRig.muzzleX = m.x;
    gunRig.muzzleY = m.y;
    out.gun = (g, gl) => {
      piece(g, 'gun', level);
      glow(gl, m.x - 0.5, m.y, 1.1, COLORS.threat, 0.45);
    };
  }

  if (RIGS[id].shield && P.shield) {
    // The shield (with the hand gripping it) is the runtime shield sprite: drawn about the grip.
    const grip = j('grip');
    out.shield = (g, gl, mode) => {
      const fwd = mode === 'bash' ? 2.5 : 0;
      piece(g, 'shield', tr(-grip.x + fwd, -grip.y));
      if (mode !== 'bash' || !J.joints.strip) return;
      const a = apply(base, { x: J.joints.strip[0], y: J.joints.strip[1] });
      const len = (t.stripLen ?? 380) * s;
      gl.save();
      gl.globalCompositeOperation = 'lighter';
      gl.strokeStyle = css(COLORS.threat, 0.75);
      gl.lineWidth = 2.6;
      gl.shadowColor = css(COLORS.threat);
      gl.shadowBlur = 8;
      gl.beginPath();
      gl.moveTo(a.x - grip.x + fwd, a.y - grip.y);
      gl.lineTo(a.x - grip.x + fwd, a.y - grip.y + len);
      gl.stroke();
      gl.restore();
    };
  }
  return out;
}

/** Painted frame drawers for an enemy, or null (procedural art). */
export function paintedEnemy(id: EnemyId, rig: FoeRig | null): PaintedEnemy | null {
  if (!rig) return null;
  try {
    if ((rig.json as { mode?: string }).mode === 'setup') return setupRig(id, rig);
    if (id === 'hornet') return hornet(rig);
    return biped(id, rig);
  } catch (err) {
    console.error(`[enemies] painted art for "${id}" failed`, err);
    return null;
  }
}
